import React from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import { CalcBoundaries } from "./CalcBoundaries";
import { CalcGeneralProps } from "./CalculationMain";
import { NumberInput } from "../framework/numberInput";
import MuiSelect from "../framework/muiSelect";
import { DailyAllowanceDateLogic } from "../../models/calculation/TESCalculationParameters";

export const CalcDailyAllowance = ({
    translate,
    settings,
    setSettings,
}: CalcGeneralProps) => {
    const {
        dailyAllowanceBoundaries,
        isDailyAllowanceCostCenterIdOn,
        isDailyAllowanceVehicleIdOn,
        isIncreasedDailyAllowanceDurationLimitOn,
        increasedDailyAllowanceDurationBoundary,
        isIncreasedDailyAllowanceOvernightLimitOn,
        increasedDailyAllowanceOvernightBoundary,
        isSeriesBasedDailyAllowanceDisabled,
        dailyAllowanceDateLogic,
        isSplitWorkShiftOnSalaryPeriodChange,
    } = settings;

    const anyIncreasedLimitOn =
        isIncreasedDailyAllowanceDurationLimitOn ||
        isIncreasedDailyAllowanceOvernightLimitOn;

    const showSharedOptions =
        anyIncreasedLimitOn || dailyAllowanceBoundaries.length > 0;

    const handleChange = (attr: string, value) => {
        setSettings({ ...settings, [attr]: value });
    };

    return (
        <Grid2 container direction="row" columnSpacing={4} flexWrap="nowrap" p={3}>
            <Grid2
                container
                direction="column"
                spacing={2}
                maxWidth={500}
                flexShrink={0}
            >
                <Grid2>
                    <Typography variant="h3" mb={0}>
                        {translate("dailyAllowance")}
                    </Typography>
                </Grid2>
                <Grid2>
                    <CalcBoundaries
                        isOn={dailyAllowanceBoundaries.length > 0}
                        boundaries={dailyAllowanceBoundaries}
                        translate={translate}
                        fillBoundaries={(val) =>
                            handleChange("dailyAllowanceBoundaries", val)
                        }
                        default50={6}
                        default100={10}
                        label50={translate("partDailyAllowance")}
                        label100={translate("dailyAllowance")}
                    />
                </Grid2>
                {showSharedOptions ? (
                    <Grid2>
                        <label className={"field-width-normal"}>
                            <Checkbox
                                id="isSeriesBasedDailyAllowanceDisabled"
                                checked={isSeriesBasedDailyAllowanceDisabled}
                                onChange={(event) =>
                                    handleChange(
                                        "isSeriesBasedDailyAllowanceDisabled",
                                        event.target.checked
                                    )
                                }
                            />
                            {translate("isSeriesBasedDailyAllowanceDisabled")}
                        </label>
                        <Alert severity="info" className="field-width-normal" sx={{ mt: 1 }}>
                            {translate("isSeriesBasedDailyAllowanceDisabledInfo")}
                        </Alert>
                    </Grid2>
                ) : null}
                {showSharedOptions ? (
                    <Grid2>
                        <label className={"field-width-normal"}>
                            <Checkbox
                                id="isDailyAllowanceCostCenterIdOn"
                                checked={isDailyAllowanceCostCenterIdOn}
                                onChange={(event) =>
                                    handleChange(
                                        "isDailyAllowanceCostCenterIdOn",
                                        event.target.checked
                                    )
                                }
                            />
                            {translate("isDailyAllowanceCostCenterIdOn")}
                        </label>
                    </Grid2>
                ) : null}
                {showSharedOptions ? (
                    <Grid2>
                        <label className={"field-width-normal"}>
                            <Checkbox
                                id="isDailyAllowanceVehicleIdOn"
                                checked={isDailyAllowanceVehicleIdOn}
                                onChange={(event) =>
                                    handleChange(
                                        "isDailyAllowanceVehicleIdOn",
                                        event.target.checked
                                    )
                                }
                            />
                            {translate("isDailyAllowanceVehicleIdOn")}
                        </label>
                    </Grid2>
                ) : null}
                {isSplitWorkShiftOnSalaryPeriodChange && showSharedOptions ? (
                    <Grid2>
                        <MuiSelect
                            label={translate("dailyAllowanceDateLogic")}
                            fullWidth={false}
                            className="field-width-normal"
                            value={dailyAllowanceDateLogic}
                            onChange={(val) =>
                                handleChange("dailyAllowanceDateLogic", val)
                            }
                            options={Object.keys(DailyAllowanceDateLogic)
                                .filter((k) => isNaN(parseInt(k)))
                                .map((o) => ({
                                    value: DailyAllowanceDateLogic[o],
                                    label: translate(
                                        `dailyAllowanceDateLogicOption.${DailyAllowanceDateLogic[o]}`
                                    ),
                                }))}
                        />
                    </Grid2>
                ) : null}
                {isSplitWorkShiftOnSalaryPeriodChange && showSharedOptions ? (
                    <Grid2>
                        <Alert severity="info" className="field-width-normal">
                            {translate("dailyAllowanceDateLogicDescription")}
                        </Alert>
                    </Grid2>
                ) : null}
            </Grid2>

            <Grid2
                container
                direction="column"
                spacing={2}
                maxWidth={400}
                flexShrink={0}
            >
                <Grid2>
                    <Typography variant="h3" mb={0}>
                        {translate("hourBookingCategory.23")}
                    </Typography>
                </Grid2>
                <Grid2>
                    <Box sx={{ width: 200 }} mt={1}>
                        <Grid2
                            container
                            rowSpacing={2}
                            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                            alignItems="center"
                        >
                            <Grid2 xs={2}>
                                <Checkbox
                                    id="isIncreasedDailyAllowanceDurationLimitOn"
                                    checked={isIncreasedDailyAllowanceDurationLimitOn}
                                    onClick={() =>
                                        handleChange(
                                            "isIncreasedDailyAllowanceDurationLimitOn",
                                            !isIncreasedDailyAllowanceDurationLimitOn
                                        )
                                    }
                                />
                            </Grid2>
                            <Grid2 xs={10}>
                                <NumberInput
                                    label={translate("increasedDailyAllowanceDurationLimit")}
                                    onChange={(value) =>
                                        handleChange(
                                            "increasedDailyAllowanceDurationBoundary",
                                            value
                                        )
                                    }
                                    value={increasedDailyAllowanceDurationBoundary}
                                    endAdornment="h"
                                    max={24}
                                    disabled={!isIncreasedDailyAllowanceDurationLimitOn}
                                />
                            </Grid2>
                            <Grid2 xs={2}>
                                <Checkbox
                                    id="isIncreasedDailyAllowanceOvernightLimitOn"
                                    checked={isIncreasedDailyAllowanceOvernightLimitOn}
                                    onClick={() =>
                                        handleChange(
                                            "isIncreasedDailyAllowanceOvernightLimitOn",
                                            !isIncreasedDailyAllowanceOvernightLimitOn
                                        )
                                    }
                                />
                            </Grid2>
                            <Grid2 xs={10}>
                                <NumberInput
                                    label={translate("increasedDailyAllowanceOvernightLimit")}
                                    onChange={(value) =>
                                        handleChange(
                                            "increasedDailyAllowanceOvernightBoundary",
                                            value
                                        )
                                    }
                                    value={increasedDailyAllowanceOvernightBoundary}
                                    endAdornment="h"
                                    max={24}
                                    disabled={!isIncreasedDailyAllowanceOvernightLimitOn}
                                />
                            </Grid2>
                        </Grid2>
                    </Box>
                </Grid2>
            </Grid2>
        </Grid2>
    );
};
